import { ColumnHelper, ColumnDef } from '@tanstack/react-table';
import { format, parseISO } from 'date-fns';

import {
  ReservationRequiredField,
  ClientPod,
  RosterListQuery,
} from '../../generated/graphql';
import getEnumLabel from '../../utilities/getEnumLabel';
import { getRoomBedsLabel } from '../../utilities/getRoomBedsLabel';
import { formatCents } from '../../utilities/money';
import TableRowCheckbox from '../Inputs/TableRowCheckbox';

import RoomInfo from './RoomInfo';

export type TableRosterRoomAssignment =
  RosterListQuery['roster']['items'][number];

export type EnumListType = Array<{ value: string; label: string }>;
export type EnumRecordType = Record<string, string>;

export interface ColumnGenerationProps {
  columnHelper: ColumnHelper<TableRosterRoomAssignment>;
  noShowStatusTypes: EnumListType | undefined;
  rosterRoomAssignmentStatuses: EnumListType | undefined;
  genders: EnumListType | undefined;
  podRequiredFields: EnumRecordType | undefined;
  availableRequiredFields?: ReservationRequiredField[];
  clientPod?: ClientPod;
  isAdmin?: boolean;
  isOnlyTraveler?: boolean;
  clientTeamMemberId?: string | number | null;
}

export const createRequiredFieldColumn = (
  {
    columnHelper,
    podRequiredFields,
    availableRequiredFields,
  }: ColumnGenerationProps,
  fieldName: ReservationRequiredField,
  headerLabelOverride?: string,
  options?: { size?: number; enableSorting?: boolean },
) => {
  // Only create column if this field is available for the current client/pod
  if (availableRequiredFields && !availableRequiredFields.includes(fieldName)) {
    return null;
  }

  return columnHelper.accessor(
    (row: TableRosterRoomAssignment) => {
      // Access the requiredFields array from the row
      const requiredFields = row.requiredFields;
      if (!requiredFields || requiredFields.length === 0) {
        return '-';
      }

      // Find the field with the matching name
      const field = requiredFields.find((f) => f.name === fieldName);
      return field?.value || '-';
    },
    {
      id: `requiredField_${fieldName}`,
      header: () => {
        const label =
          headerLabelOverride ||
          (podRequiredFields && podRequiredFields[fieldName]) ||
          fieldName;
        return label.toLocaleUpperCase();
      },
      cell: (info) => <>{info.getValue()}</>,
      enableSorting: options?.enableSorting ?? false,
      size: options?.size,
    },
  );
};

export const getSelectColumn = ({
  columnHelper,
  isAdmin,
  isOnlyTraveler,
  clientTeamMemberId,
}: ColumnGenerationProps) => {
  // Show select column for admins OR travelers (with self-restriction)
  if (!isAdmin && !isOnlyTraveler) {
    return null;
  }

  return columnHelper.display({
    size: 100,
    id: 'select',
    header: ({ table }) => {
      // Only show "select all" for admins, not travelers
      if (!isAdmin) return null;
      return (
        <TableRowCheckbox
          {...{
            checked: table.getIsAllRowsSelected(),
            indeterminate: table.getIsSomeRowsSelected(),
            onChange: table.getToggleAllRowsSelectedHandler(),
          }}
        />
      );
    },
    cell: ({ row }) => {
      // For travelers, only show checkbox for their own row
      if (isOnlyTraveler && !isAdmin) {
        const isOwnRow =
          clientTeamMemberId &&
          String(row.original.guest?.id) === String(clientTeamMemberId);
        if (!isOwnRow) return null;
      }

      return (
        <TableRowCheckbox
          {...{
            checked: row.getIsSelected(),
            disabled: !row.getCanSelect(),
            indeterminate: row.getIsSomeSelected(),
            onChange: row.getToggleSelectedHandler(),
          }}
        />
      );
    },
  });
};

export const getGuestNameColumn = (
  { columnHelper }: ColumnGenerationProps,
  header = 'FULL NAME (LAST, FIRST)',
) =>
  columnHelper.accessor('guest', {
    size: 225,
    header: () => header.toLocaleUpperCase(),
    cell: (info) => {
      const guest = info.getValue();
      if (!guest) return '-';
      const displayName = `${guest.lastName || ''}, ${guest.firstName || ''}`;
      return <>{displayName}</>;
    },
  });

export const getGuest2NameColumn = (
  { columnHelper }: ColumnGenerationProps,
  header = 'GUEST 2 (LAST, FIRST)',
) =>
  columnHelper.accessor('roommates', {
    size: 225,
    header: () => header.toLocaleUpperCase(),
    enableSorting: false,
    cell: (info) => {
      const roommates = info.getValue();
      const primaryGuest = info.row.original.guest;

      if (!roommates || roommates.length === 0) return '-';

      // Filter out the primary guest from roommates
      const otherRoommates = roommates.filter(
        (roommate) => roommate.id !== primaryGuest?.id,
      );

      if (otherRoommates.length === 0) return '-';

      const secondGuest = otherRoommates[0];
      const displayName = `${secondGuest.lastName || ''}, ${secondGuest.firstName || ''}`;
      return <>{displayName}</>;
    },
  });

export const getStatusColumn = ({
  columnHelper,
  rosterRoomAssignmentStatuses,
}: ColumnGenerationProps) =>
  columnHelper.accessor('status', {
    header: 'STATUS',
    cell: (info) => {
      const status = info.getValue();
      return status
        ? getEnumLabel(rosterRoomAssignmentStatuses || [], status)
        : '-';
    },
  });

export const getPropertyColumn = ({ columnHelper }: ColumnGenerationProps) =>
  columnHelper.accessor('hotel.name', {
    size: 275,
    header: 'PROPERTY',
    cell: (info) => <>{info.getValue() ?? '-'}</>,
  });

export const getAddressColumn = ({ columnHelper }: ColumnGenerationProps) =>
  columnHelper.accessor('hotel.address', {
    enableSorting: false,
    header: 'ADDRESS',
    cell: (info) => {
      const address = info.getValue();
      if (!address) return '-';
      const { addressLine1, addressLine2 } = address;
      if (!addressLine1 && !addressLine2) return '-';
      return `${address.addressLine1 || ''} ${
        address.addressLine2 || ''
      }`.trim();
    },
  });

export const getCityColumn = ({ columnHelper }: ColumnGenerationProps) =>
  columnHelper.accessor('hotel.address.city', {
    header: 'CITY',
    cell: (info) => <>{info.getValue() ?? '-'}</>,
  });

export const getStateColumn = ({ columnHelper }: ColumnGenerationProps) =>
  columnHelper.accessor('hotel.address.state', {
    header: 'STATE',
    cell: (info) => <>{info.getValue() ?? '-'}</>,
  });

export const getRoomNumberColumn = ({ columnHelper }: ColumnGenerationProps) =>
  columnHelper.accessor('hotelRoomNumber', {
    header: 'ROOM #',
    cell: (info) => <RoomInfo info={info} />,
  });

export const getRoomTypeColumn = ({ columnHelper }: ColumnGenerationProps) =>
  columnHelper.accessor('roomBeds', {
    enableSorting: false,
    header: 'ROOM TYPE',
    cell: (info) => {
      const value = info.getValue();
      return <>{getRoomBedsLabel(value)}</>;
    },
  });

export const getHotelConfirmationNumberColumn = ({
  columnHelper,
}: ColumnGenerationProps) =>
  columnHelper.accessor('hotelConfirmationNumber', {
    enableSorting: false,
    header: 'CONFIRMATION #',
    cell: (info) => <>{info.getValue() ?? '-'}</>,
  });

export const getCheckInDateColumn = (
  { columnHelper }: ColumnGenerationProps,
  dateFormatString = 'M/d/yyyy',
) =>
  columnHelper.accessor('checkInDate', {
    header: 'CHECK-IN',
    cell: (info) => {
      const val = info.getValue();
      if (!val) return '-';
      try {
        const date = parseISO(val);
        return <>{format(date, dateFormatString)}</>;
      } catch {
        return <>{val}</>;
      }
    },
  });

export const getCheckOutDateColumn = (
  { columnHelper }: ColumnGenerationProps,
  dateFormatString = 'M/d/yyyy',
) =>
  columnHelper.accessor('checkOutDate', {
    header: 'CHECK-OUT',
    cell: (info) => {
      const val = info.getValue();
      if (!val) return '-';
      try {
        const date = parseISO(val);
        return <>{format(date, dateFormatString)}</>;
      } catch {
        return <>{val}</>;
      }
    },
  });

export const getClientRateColumn = ({
  columnHelper,
  isAdmin,
}: ColumnGenerationProps) => {
  // Only show CLIENT RATE column for admin users
  if (!isAdmin) {
    return null;
  }

  return columnHelper.accessor('ngRate', {
    enableSorting: false,
    header: 'RATE',
    cell: (info) => {
      const ngRate = info.getValue();
      const quotedRate = info.row.original.quotedRate;

      // Fallback strategy: ngRate → quotedRate → "-"
      if (ngRate !== null && ngRate !== undefined) {
        return <>{formatCents(ngRate)}</>;
      } else if (quotedRate !== null && quotedRate !== undefined) {
        return <>{formatCents(quotedRate)}</>;
      } else {
        return <>-</>;
      }
    },
  });
};

export const getNoShowStatusColumn = ({
  columnHelper,
  noShowStatusTypes,
}: ColumnGenerationProps) =>
  columnHelper.accessor('noShowStatus', {
    header: 'NS OR CANCEL',
    cell: (info) => {
      const noShowStatus = info.getValue();
      return noShowStatus
        ? getEnumLabel(noShowStatusTypes || [], noShowStatus)
        : '-';
    },
  });

export const getDuplicateApprovalColumn = ({
  columnHelper,
}: ColumnGenerationProps) =>
  columnHelper.accessor('duplicateApproval', {
    header: 'DUPLICATE APPROVED',
    enableSorting: false,
    cell: (info) => {
      const value = info.getValue();
      if (!value) return <>-</>;
      if (value.__typename === 'DuplicateApprovalBlanket') {
        return <>Blanket approval</>;
      }
      const approver = value.approver;
      if (!approver) return <>Approved by client</>;
      return (
        <>
          Approved by {approver.firstName} {approver.lastName}
        </>
      );
    },
  });

export const getReservationNumberColumn = ({
  columnHelper,
}: ColumnGenerationProps) =>
  columnHelper.accessor((row) => row?.order?.orderNumber, {
    id: 'reservationNumber',
    enableSorting: false,
    header: 'RESERVATION #',
    cell: (info) => <>{info.getValue() ?? '-'}</>,
  });

export const getThsPoNumberColumn = ({ columnHelper }: ColumnGenerationProps) =>
  columnHelper.accessor((row) => row?.order?.orderNumber, {
    id: 'thsPoNumber',
    enableSorting: false,
    header: 'THS PO #',
    cell: (info) => <>{info.getValue() ?? '-'}</>,
  });

// Helper function to get the available required fields for a pod
export const getAvailableRequiredFields = (
  podRequiredFields:
    | Record<ClientPod, { value: ReservationRequiredField; label: string }[]>
    | undefined,
  clientPod: ClientPod,
): ReservationRequiredField[] => {
  if (!podRequiredFields || !podRequiredFields[clientPod]) {
    return [];
  }
  return podRequiredFields[clientPod].map((field) => field.value);
};

// Energy View Columns
export const getEnergyViewColumns = (
  props: ColumnGenerationProps,
): ColumnDef<TableRosterRoomAssignment, unknown>[] => {
  const columns = [
    getSelectColumn(props),
    getGuestNameColumn(props, 'GUEST 1 (LAST, FIRST)'),
    getStatusColumn(props),
    getRoomTypeColumn(props),
    getRoomNumberColumn(props),
    getCheckInDateColumn(props, 'MM/dd/yyyy'),
    getCheckOutDateColumn(props, 'MM/dd/yyyy'),
    getPropertyColumn(props),
    getAddressColumn(props),
    getCityColumn(props),
    getStateColumn(props),
    getClientRateColumn(props),
    getHotelConfirmationNumberColumn(props),
    getDuplicateApprovalColumn(props),
    createRequiredFieldColumn(props, ReservationRequiredField.PoOrGl),
    createRequiredFieldColumn(props, ReservationRequiredField.ApproverId),
    createRequiredFieldColumn(props, ReservationRequiredField.JobNumber),
    createRequiredFieldColumn(props, ReservationRequiredField.JobProjectName),
    createRequiredFieldColumn(props, ReservationRequiredField.TrfNumber),
    createRequiredFieldColumn(props, ReservationRequiredField.Customer),
    createRequiredFieldColumn(props, ReservationRequiredField.LocationBase),
    createRequiredFieldColumn(props, ReservationRequiredField.Well),
    createRequiredFieldColumn(props, ReservationRequiredField.CostCenter),
    createRequiredFieldColumn(props, ReservationRequiredField.EmployeeNumber),
    createRequiredFieldColumn(props, ReservationRequiredField.WorkOrder),
    createRequiredFieldColumn(props, ReservationRequiredField.OrderedBy),
    createRequiredFieldColumn(props, ReservationRequiredField.ContactInfo),
    createRequiredFieldColumn(props, ReservationRequiredField.FleetNumber),
    createRequiredFieldColumn(props, ReservationRequiredField.District),
    createRequiredFieldColumn(props, ReservationRequiredField.Crew),
    createRequiredFieldColumn(
      props,
      ReservationRequiredField.JobTitle,
      'JOB TITLE',
    ),
    createRequiredFieldColumn(
      props,
      ReservationRequiredField.DoorCode,
      'ROOM CODE',
    ),
    createRequiredFieldColumn(props, ReservationRequiredField.UnitCode),
    createRequiredFieldColumn(props, ReservationRequiredField.DepartmentCode),
    getThsPoNumberColumn(props),
  ];

  // Filter out null columns
  return columns.filter(
    (column): column is ColumnDef<TableRosterRoomAssignment, unknown> =>
      column !== null,
  );
};

// Government View Columns
export const getGovernmentViewColumns = (
  props: ColumnGenerationProps,
): ColumnDef<TableRosterRoomAssignment, unknown>[] => {
  const columns = [
    getSelectColumn(props),
    getGuestNameColumn(props, 'GUEST NAME (LAST, FIRST)'),
    createRequiredFieldColumn(props, ReservationRequiredField.DodId),
    createRequiredFieldColumn(props, ReservationRequiredField.Rank),
    createRequiredFieldColumn(props, ReservationRequiredField.Mission),
    createRequiredFieldColumn(props, ReservationRequiredField.TaskForce),
    createRequiredFieldColumn(props, ReservationRequiredField.Gender),
    getReservationNumberColumn(props),
    getStatusColumn(props),
    getPropertyColumn(props),
    getAddressColumn(props),
    getCityColumn(props),
    getStateColumn(props),
    getRoomNumberColumn(props),
    getRoomTypeColumn(props),
    getHotelConfirmationNumberColumn(props),
    getCheckInDateColumn(props, 'yyyy/MM/dd'),
    getCheckOutDateColumn(props, 'yyyy/MM/dd'),
    getClientRateColumn(props),
    getNoShowStatusColumn(props),
    getDuplicateApprovalColumn(props),
    createRequiredFieldColumn(
      props,
      ReservationRequiredField.JobTitle,
      'JOB TITLE',
    ),
    createRequiredFieldColumn(
      props,
      ReservationRequiredField.DoorCode,
      'ROOM CODE',
    ),
  ];

  // Filter out null columns
  return columns.filter(
    (column): column is ColumnDef<TableRosterRoomAssignment, unknown> =>
      column !== null,
  );
};
